import {
  DEFAULT_MOLECULE_EXPORT_DIMENSIONS,
  DEFAULT_REACTION_EXPORT_DIMENSIONS,
  MOLECULE_LAYOUT_BASE,
  REACTION_LAYOUT_BASE,
  getUniformSvgScale,
  type ExportDimensions,
} from './exportLayout';
import { getStableReactionArrowClusterScale } from './reactionArrowScale';
import { measureReactionArrowColumnWidth } from './measureReactionArrowLayout';

export type ArrowTypeSingleDouble = 'single' | 'double';

/** Same row layout as ExportableScheme for a given export size. */
export function computeReactionStageLayout(
  exportSize: ExportDimensions,
  topText: string,
  bottomText: string,
  arrowType: ArrowTypeSingleDouble,
  reactantViewBox: string | undefined,
  reactantZoom: number,
  arrowClusterScaleProp: number | null | undefined,
  /** Multiplies arrow column typography/measurement vs base cluster (SVG-native scheme zoom). */
  schemeDisplayZoom: number = 1
): {
  contentHeight: number;
  moleculeWidth: number;
  arrowWidth: number;
  clusterScale: number;
} {
  const clusterScale =
    arrowClusterScaleProp !== undefined && arrowClusterScaleProp !== null
      ? arrowClusterScaleProp
      : getStableReactionArrowClusterScale(reactantViewBox, reactantZoom);

  const arrowMeasureScale = schemeDisplayZoom;

  const arrowWidth = measureReactionArrowColumnWidth(
    topText,
    bottomText,
    arrowMeasureScale,
    arrowType
  );

  const stagePadding = REACTION_LAYOUT_BASE.padding;
  const stageGap = REACTION_LAYOUT_BASE.gap;
  const contentHeight = Math.max(64, exportSize.height - stagePadding * 2);
  const availableWidthForMolecules = Math.max(
    128,
    exportSize.width - stagePadding * 2 - stageGap * 2 - arrowWidth
  );
  const moleculeWidth = Math.max(64, availableWidthForMolecules / 2);

  return { contentHeight, moleculeWidth, arrowWidth, clusterScale };
}

/** Layout at default reaction export size — reference for atom-scale compensation and fixed molecule column width. */
export function computeReferenceReactionStageLayout(
  topText: string,
  bottomText: string,
  arrowType: ArrowTypeSingleDouble,
  reactantViewBox: string | undefined,
  reactantZoom: number,
  arrowClusterScaleProp?: number | null
): ReturnType<typeof computeReactionStageLayout> {
  return computeReactionStageLayout(
    DEFAULT_REACTION_EXPORT_DIMENSIONS,
    topText,
    bottomText,
    arrowType,
    reactantViewBox,
    reactantZoom,
    arrowClusterScaleProp
  );
}

const COMP_MIN = 0.05;
const COMP_MAX = 20;

/**
 * Scale factor so apparent bond/atom size matches reference slot geometry.
 * Visual size ∝ getUniformSvgScale * compensation = refScale (when zoom unchanged).
 */
export function getMoleculeScaleCompensation(
  viewBox: string | undefined,
  refSlotWidth: number,
  refSlotHeight: number,
  curSlotWidth: number,
  curSlotHeight: number
): number {
  const refS = getUniformSvgScale(viewBox, refSlotWidth, refSlotHeight);
  const curS = getUniformSvgScale(viewBox, curSlotWidth, curSlotHeight);
  if (curS < 1e-9) return 1;
  const raw = refS / curS;
  return Math.min(COMP_MAX, Math.max(COMP_MIN, raw));
}

export function computeSingleMoleculeSlot(exportSize: ExportDimensions): {
  moleculeWidth: number;
  moleculeHeight: number;
} {
  const stagePadding = MOLECULE_LAYOUT_BASE.padding;
  return {
    moleculeWidth: Math.max(64, exportSize.width - stagePadding * 2),
    moleculeHeight: Math.max(64, exportSize.height - stagePadding * 2),
  };
}

export function getSingleMoleculeScaleCompensation(
  viewBox: string | undefined,
  curExport: ExportDimensions
): number {
  const ref = computeSingleMoleculeSlot(DEFAULT_MOLECULE_EXPORT_DIMENSIONS);
  const cur = computeSingleMoleculeSlot(curExport);
  return getMoleculeScaleCompensation(
    viewBox,
    ref.moleculeWidth,
    ref.moleculeHeight,
    cur.moleculeWidth,
    cur.moleculeHeight
  );
}
